'use client';
import { jsx as _jsx } from "react/jsx-runtime";
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { createContext, useEffect, useMemo } from 'react';
import { createTrackedSelector } from 'react-tracked';
import { useStore } from 'zustand';
/**
 * Creates a simple typed Zustand context with basic hooks for React components.
 * This is a simpler version of createZustandContext that doesn't handle initial values.
 * Ensures stores are request-scoped for server-side rendering compatibility.
 *
 * @param name - The name of the context (used in error messages)
 * @returns Object containing Context and hooks for accessing the store
 *
 * @example
 * // Define your store type with grouped actions
 * interface SimpleStore {
 *   // State
 *   count: number;
 *   // Group actions in an object
 *   actions: {
 *     increment: () => void;
 *     decrement: () => void;
 *   };
 * }
 *
 * // Create the store
 * const createSimpleStore = () => create<SimpleStore>((set) => ({
 *   count: 0,
 *   actions: {
 *     increment: () => set((state) => ({ count: state.count + 1 })),
 *     decrement: () => set((state) => ({ count: state.count - 1 })),
 *   },
 * }));
 *
 * // Create the context and hooks
 * const {
 *   Context: SimpleStoreContext,
 *   useTrackedState,
 * } = createZustandStoreTypedReactContext<SimpleStore>('SimpleStore');
 *
 * // Use in components:
 * const App = () => {
 *   // Create store instance within component to ensure request-scoped stores
 *   const [store] = React.useState(createSimpleStore);
 *
 *   return (
 *     <SimpleStoreContext.Provider value={store}>
 *       <Counter />
 *     </SimpleStoreContext.Provider>
 *   );
 * };
 *
 * const Counter = () => {
 *   const { count, actions } = useTrackedState();
 *
 *   return (
 *     <div>
 *       Count: {count}
 *       <button onClick={actions.increment}>+</button>
 *       <button onClick={actions.decrement}>-</button>
 *     </div>
 *   );
 * };
 *
 * @best-practices
 * 1. Group actions in an 'actions' object for better organization
 * 2. Create specific selector hooks instead of using useSelector directly
 * 3. Follow naming convention: [Store]Context for the Context
 * 4. Create separate hooks for state and actions
 * 5. Keep selectors colocated with store definition
 * 6. Always create store instance using useState within components
 * 7. Never create stores in the module scope to prevent SSR issues
 */
export function createZustandStoreTypedReactContext(name) {
    const Context = createContext(null);
    Context.displayName = name;
    const { useSelector, useStore, useTrackedState } = createStoreHooks(Context, name);
    return {
        Context,
        useSelector,
        useStore,
        useTrackedState,
    };
}
function createStoreHooks(Context, name) {
    const useStore = () => {
        const store = React.useContext(Context);
        if (!store) {
            throw new Error(`This hook must be used within ${name}.Provider`);
        }
        return store;
    };
    const useSelector = (selector) => {
        const store = useStore();
        return store(selector);
    };
    const useTrackedState = createTrackedSelector(useSelector);
    return {
        useSelector,
        useStore,
        useTrackedState,
    };
}
/**
 * Creates a derived vanilla store from a source store.
 * The derived store computes values lazily and is read-only.
 * Chainable: derive(derive(store, fn1), fn2) works.
 * @param store - The source store to derive from
 * @param deriveFn - The function to derive the new state from the source state
 * @returns A tuple containing the derived store and an unsubscribe function
 */
export function derive(store, deriveFn) {
    const listeners = new Set();
    const cache = {
        source: undefined,
        derived: undefined,
    };
    // Lazily compute derived state
    const getDerivedState = () => {
        const source = store.getState();
        if (cache.derived === undefined || cache.source !== source) {
            cache.source = source;
            cache.derived = deriveFn(source);
        }
        return cache.derived;
    };
    // Subscribe to source store
    const unsubscribe = store.subscribe((state, prevState) => {
        const derived = deriveFn(state);
        // Reuse the cached result for the previous state instead of deriving it a
        // second time on every source update.
        const prev = cache.derived !== undefined && cache.source === prevState
            ? cache.derived
            : deriveFn(prevState);
        // Subscribers can synchronously call getState(), which must reuse this snapshot.
        cache.source = state;
        cache.derived = derived;
        // Notify listeners even if derived values are the same reference
        // (let consumers decide with their own equality checks)
        listeners.forEach((listener) => listener(derived, prev));
    });
    return [
        {
            getState: getDerivedState,
            getInitialState: () => deriveFn(store.getInitialState()),
            subscribe: (listener) => {
                listeners.add(listener);
                return () => listeners.delete(listener);
            },
        },
        unsubscribe,
    ];
}
/**
 * React hook binding for a derived store.
 * @param store - The source store to derive from
 * @param deriveFn - The function to derive the new state from the source state
 * @returns The derived store
 */
function useDerived(store, deriveFn) {
    const [derived, unsubscribe] = useMemo(() => derive(store, deriveFn), [store, deriveFn]);
    useEffect(() => () => unsubscribe(), [unsubscribe]);
    return derived;
}
/**
 * React hooks for reading from a derived store.
 * @param store - The source store to derive from
 * @param deriveFn - The function to derive the new state from the source state
 * @returns A useProxy hook and useSelector hook
 */
export function useDerivedHooks(store, deriveFn) {
    const derived = useDerived(store, deriveFn);
    return useMemo(() => {
        const useSelector = (selector) => useStore(derived, selector);
        return {
            useSelector,
            useProxy: createTrackedSelector(useSelector),
        };
    }, [derived]);
}
/**
 * Creates a typed Zustand context with Provider and hooks for React components.
 * This utility helps manage global state with proper TypeScript support and React context.
 * Uses react-tracked for automatic re-render optimization.
 *
 * @param name - The name of the context (used in error messages)
 * @param getStore - A function that creates the Zustand store, optionally accepting initial values
 * @returns Object containing Provider, Context, and hooks
 *
 * @example
 * // Define your store type with grouped actions
 * interface CounterStore {
 *   // State
 *   count: number;
 *   // Group actions in an object
 *   actions: {
 *     increment: () => void;
 *     decrement: () => void;
 *   };
 * }
 *
 * // Create the store
 * const createCounterStore = (initial?: { count: number }) =>
 *   create<CounterStore>((set) => ({
 *     count: initial?.count ?? 0,
 *     actions: {
 *       increment: () => set((state) => ({ count: state.count + 1 })),
 *       decrement: () => set((state) => ({ count: state.count - 1 })),
 *     },
 *   }));
 *
 * // Create the context and hooks
 * const {
 *   Provider: CounterStoreProvider,
 *   useTrackedState,
 * } = createZustandContext<StoreApi<CounterStore>, { count: number }>(
 *   'CounterStore',
 *   createCounterStore
 * );
 *
 *
 * // Use in components:
 * const App = () => (
 *   <CounterStoreProvider initialValue={{ count: 0 }}>
 *     <Counter />
 *   </CounterStoreProvider>
 * );
 *
 * const Counter = () => {
 *   const { increment, decrement, actions } = useTrackedState();
 *
 *   return (
 *     <div>
 *       Count: {count}
 *       <button onClick={actions.increment}>+</button>
 *       <button onClick={actions.decrement}>-</button>
 *     </div>
 *   );
 * };
 *
 * @best-practices
 * 1. Use useTrackedState in 99% of cases when you need to access any state
 * 2. Use useSelector rarely and only for special use cases where you know what you're doing
 * 3. Group actions in an 'actions' object for better organization
 * 4. Use memo on dumber components to prevent unnecessary re-renders
 * 5. Keep selectors colocated with store definition
 */
export const createZustandContext = (name, getStore) => {
    const Context = createContext(null);
    Context.displayName = name;
    const Provider = (props) => {
        const [store] = React.useState(() => props.initialValue ? getStore(props?.initialValue) : getStore(undefined));
        return _jsx(Context.Provider, { value: store, children: props.children });
    };
    const { useSelector, useStore, useTrackedState } = createStoreHooks(Context, name);
    return {
        useSelector,
        useStore,
        useTrackedState,
        Context,
        Provider,
    };
};
//# sourceMappingURL=createZustandContext.js.map